import React, { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';
import Modal from './Modal';
import { Box, TextField, Grid, Button, MenuItem } from '@mui/material';
import { makeApiRequest } from '../../Shared/Api';

const REMINDER_INTERVAL_OPTIONS = [
	{ value: 0, label: 'Off' },
	{ value: 1, label: 'Every day' },
	{ value: 2, label: 'Every 2 days' },
	{ value: 3, label: 'Every 3 days' },
	{ value: 5, label: 'Every 5 days' },
	{ value: 7, label: 'Every 7 days' },
];

const EMPTY_FORM = {
	name: '',
	invoice_payee: '',
	invoice_mailing_address: '',
	venmo_username: '',
	from_email: '',
	phone: '',
	tax_id: '',
	bid_reminder_interval_days: 2,
};

const EditOrganization = ({
	open,
	onClose,
	onSubmit,
	selectedOrganization,
}) => {
	const { enqueueSnackbar } = useSnackbar();
	const [formData, setFormData] = useState(EMPTY_FORM);
	const isEditing = Boolean(selectedOrganization);

	useEffect(() => {
		if (selectedOrganization) {
			setFormData({
				name: selectedOrganization.name || '',
				invoice_payee: selectedOrganization.invoice_payee || '',
				invoice_mailing_address:
					selectedOrganization.invoice_mailing_address || '',
				venmo_username: selectedOrganization.venmo_username || '',
				from_email: selectedOrganization.from_email || '',
				phone: selectedOrganization.phone || '',
				tax_id: selectedOrganization.tax_id || '',
				bid_reminder_interval_days:
					selectedOrganization.bid_reminder_interval_days ?? 2,
			});
		} else {
			setFormData(EMPTY_FORM);
		}
	}, [selectedOrganization, open]);

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({ ...prev, [name]: value }));
	};

	const buildPayload = () => ({
		name: formData.name,
		invoice_payee: formData.invoice_payee,
		invoice_mailing_address: formData.invoice_mailing_address,
		venmo_username: formData.venmo_username,
		from_email: formData.from_email,
		phone: formData.phone,
		tax_id: formData.tax_id,
		bid_reminder_interval_days: formData.bid_reminder_interval_days,
	});

	const handleSubmit = async () => {
		try {
			const payload = buildPayload();
			if (isEditing) {
				await makeApiRequest(
					'PATCH',
					`/auth/organizations/${selectedOrganization.id}/`,
					payload,
				);
			} else {
				await makeApiRequest('POST', '/auth/organizations/', payload);
			}
			if (onSubmit) onSubmit();
			onClose();
		} catch (error) {
			enqueueSnackbar(
				error.message ||
					`Failed to ${isEditing ? 'update' : 'create'} organization.`,
				{ variant: 'error' },
			);
		}
	};

	return (
		<Modal
			open={open}
			onClose={onClose}
			useActionButtons
			actionButtons={
				<Button variant="contained" onClick={handleSubmit}>
					{isEditing ? 'Save Organization' : 'Create Organization'}
				</Button>
			}
			modalTitle={isEditing ? 'Edit Organization' : 'Create Organization'}
			maxWidth="sm"
		>
			<Box sx={{ mt: 2 }}>
				<Grid container spacing={2}>
					<TextField
						label="Name"
						name="name"
						value={formData.name}
						onChange={handleInputChange}
						fullWidth
						required
						helperText={
							'Changing the name will update the system-generated slug'
						}
					/>
					<TextField
						label="Invoice payee"
						name="invoice_payee"
						value={formData.invoice_payee}
						onChange={handleInputChange}
						fullWidth
						required
					/>
					<TextField
						label="Invoice mailing address"
						name="invoice_mailing_address"
						value={formData.invoice_mailing_address}
						onChange={handleInputChange}
						fullWidth
						required
						multiline
						minRows={3}
					/>
					<TextField
						label="Venmo username"
						name="venmo_username"
						value={formData.venmo_username}
						onChange={handleInputChange}
						fullWidth
						placeholder="your-business"
						helperText="Shown on deposits and invoices up to $2,500."
					/>
					<TextField
						label="From email"
						name="from_email"
						type="email"
						value={formData.from_email}
						onChange={handleInputChange}
						fullWidth
						placeholder="billing@yourbusiness.com"
						helperText="Reply-to for customer emails."
					/>
					<TextField
						label="Phone"
						name="phone"
						value={formData.phone}
						onChange={handleInputChange}
						fullWidth
						placeholder="(801) 555-1234"
					/>
					<TextField
						label="Tax ID / EIN"
						name="tax_id"
						value={formData.tax_id}
						onChange={handleInputChange}
						fullWidth
						placeholder="12-3456789"
					/>
					<TextField
						select
						label="Bid reminder frequency"
						name="bid_reminder_interval_days"
						value={formData.bid_reminder_interval_days}
						onChange={(e) =>
							setFormData((prev) => ({
								...prev,
								bid_reminder_interval_days: Number(e.target.value),
							}))
						}
						fullWidth
						helperText="How often pending bids get a reminder to review the proposal."
					>
						{REMINDER_INTERVAL_OPTIONS.map((option) => (
							<MenuItem key={option.value} value={option.value}>
								{option.label}
							</MenuItem>
						))}
					</TextField>
				</Grid>
			</Box>
		</Modal>
	);
};

export default EditOrganization;
